import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

export type Theme = 'system' | 'light' | 'dark';
/** Pages in a centred column, or across the whole screen beside the menu. */
export type PageWidth = 'boxed' | 'full';

/**
 * Light, dark, or whatever the device uses. Kept per browser; applied as
 * data-theme on <html>, which the tokens in styles/tokens.css follow. The page
 * width is kept the same way.
 */
export const useTheme = defineStore('theme', () => {
  const theme = ref<Theme>('system');
  const systemDark = ref(window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
  window
    .matchMedia?.('(prefers-color-scheme: dark)')
    .addEventListener('change', (e) => (systemDark.value = e.matches));

  /** What is on screen now, whichever way it was chosen. */
  const effective = computed<'light' | 'dark'>(() =>
    theme.value === 'system' ? (systemDark.value ? 'dark' : 'light') : theme.value,
  );

  function set(next: Theme) {
    theme.value = next;
    if (next === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem('ismtur.theme', next);
    } catch {
      /* private mode: the choice lasts this visit only */
    }
  }

  const width = ref<PageWidth>('boxed');
  function setWidth(next: PageWidth) {
    width.value = next;
    try {
      localStorage.setItem('ismtur.width', next);
    } catch {
      /* private mode: the choice lasts this visit only */
    }
  }

  function restore() {
    try {
      const saved = localStorage.getItem('ismtur.theme') as Theme | null;
      if (saved === 'light' || saved === 'dark' || saved === 'system') set(saved);
      const w = localStorage.getItem('ismtur.width');
      if (w === 'boxed' || w === 'full') width.value = w;
    } catch {
      /* storage unavailable */
    }
  }

  // The phone's status bar and browser chrome take the colour of the top bar.
  watch(
    effective,
    (mode) => {
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', mode === 'dark' ? '#181c23' : '#ffffff');
    },
    { immediate: true },
  );

  /** The one-click switch: to the other of light and dark. */
  const toggle = () => set(effective.value === 'dark' ? 'light' : 'dark');

  return { theme, effective, set, restore, toggle, width, setWidth };
});
